import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { RiDeleteBin6Line } from "@remixicon/react";
import { Button } from "@/web/components/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/web/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/web/components/ui/form";
import { Input } from "@/web/components/ui/input";
import type { CategoryInput } from "@/types";

// Zod schema for category form validation
const categoryFormSchema = z.object({
  uid: z.number().optional(),
  name: z.string().min(3, "Category name is required").max(255, "Category name must be less than 255 characters"),
  imageUrl: z.string().url("Please enter a valid URL").optional().or(z.literal('')),
  parentId: z.string().optional(),
  parentName: z.string().optional(),
  description: z.string().optional().or(z.literal('')),
});

type CategoryFormData = z.infer<typeof categoryFormSchema>;

export interface CategoryFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  category?: CategoryInput;
  onSubmit: (data: CategoryFormData) => void;
  onDelete?: (categoryUid: number | null | undefined) => void;
  isLoading?: boolean;
}

export function CategoryFormModal({
  isOpen,
  onOpenChange,
  mode,
  category,
  onSubmit,
  onDelete,
  isLoading = false,
}: CategoryFormModalProps) {
  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: category?.name || '',
      imageUrl: category?.imageUrl || '',
      description: category?.description || '',
    },
  });

  // Reset form when category data changes or modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      form.reset({
        name: category?.name || '',
        imageUrl: category?.imageUrl || '',
        description: category?.description || '',
      });
    }
  }, [isOpen, category, form]);

  const handleSubmit = (data: CategoryFormData) => {
    onSubmit(data);
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (onDelete && category) {
      onDelete(category.uid);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create New Category" : "Edit Category"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Add a new category to your database."
              : "Make changes to the category information."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter category name"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="imageUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Image URL</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="Enter image URL (optional)"
                      {...field}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              {mode === "edit" && onDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="self-start"
                >
                  <RiDeleteBin6Line className="size-4" />
                </Button>
              )}
              
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:ml-auto">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading
                    ? mode === "create"
                      ? "Creating..."
                      : "Saving..."
                    : mode === "create"
                    ? "Create"
                    : "Save"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
